import { motion } from "framer-motion";

const STEPS = [
  { n: "01", t: "Discover", d: "Deep-dive into your vision. We uncover goals, users, and constraints — so we're solving the right problem before writing a single line of code." },
  { n: "02", t: "Design", d: "Wireframes, user flows, and interactive prototypes. We validate assumptions early so engineering can move fast and with conviction." },
  { n: "03", t: "Build", d: "Tight sprints, daily progress updates, and production quality from the start. No shortcuts, no tech debt landmines hiding in the codebase." },
  { n: "04", t: "Test", d: "Unit, integration, E2E, and AI model evaluation. We test across the full stack — and we don't ship what we aren't confident in." },
  { n: "05", t: "Launch", d: "CI/CD pipelines, observability, and a hands-on launch window. We stay close through go-live to make sure everything holds under real traffic." },
  { n: "06", t: "Scale", d: "Post-launch is where most studios disappear. We double down — analytics-driven iteration, infra hardening, and roadmapping to stay ahead." },
];

export default function Process() {
  return (
    <section
      id="process"
      className="relative py-24 md:py-32 border-y border-white/5"
      data-testid="process-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            / 04 — How we work
          </div>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              visible: { transition: { staggerChildren: 0.1 } },
            }}
            className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="process-heading"
          >
            <motion.span 
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } }} 
              className="block"
            >
              How we work.
            </motion.span>
            <motion.span 
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } }} 
              className="block text-zinc-500"
            >
              Idea to production.
            </motion.span>
          </motion.h2>
        </div>

        <div className="relative">
          {/* timeline track */}
          <div className="hidden md:block absolute top-6 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="relative"
                data-testid={`process-step-${i}`}
              >
                {/* node */}
                <div className="hidden md:flex absolute -top-[30px] left-0 w-3 h-3 rounded-full bg-ink-0 border border-accent-cyan/50 items-center justify-center">
                  <div className="w-1 h-1 rounded-full bg-accent-cyan" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                  STEP {s.n}
                </div>
                <h3 className="font-display text-xl md:text-2xl tracking-tight mb-2">
                  {s.t}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
